import {
    LINE_ITEM_TYPE_CARD,
    MEDIUM_TYPE_PHYSICAL,
} from '@/prisma/schemaConstants'
import { CUSTOM_CARD_IMAGE } from '@/src/common/constants'
import { useCartContext } from '@/src/common/contexts/CartContext'
import useGetCartManually from '@/src/common/hooks/useGetCartManually'
import usePostAddCart from '@/src/common/hooks/usePostAddCart'
import checkKYCMinMaxLimits from '@/src/common/utilities/checkKYCMinMaxLimits'
import getDefaultDenom from '@/src/common/utilities/getDefaultDenom'
import getDefaultPredesign from '@/src/common/utilities/getDefaultPredesign'
import { dataLayerAddVisaToCart } from '@/src/common/utilities/gtag'
import {
    IBACPredesign,
    IBACProduct,
    ILineItemWithoutResolvedData,
    TKYCMinMaxValidations,
} from '@/src/modules/build-a-card/types'
import updateExistingBoxData from '@/src/modules/build-a-card/utilities/updateExistingBoxData'
import { ICart, IEmptyCart, ILineItem } from '@/src/modules/cart/ICart'
import { BoxStep1Schema } from '@/src/modules/gift-box/schemas'
import round from 'lodash/round'
import { useRouter } from 'next/router'
import {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react'
import { QueryObserverResult } from 'react-query'
import { v4 as uuid } from 'uuid'
import { ValidationError } from 'yup'
import {
    checkIfExistingDeliveryForPlastic,
    checkIfMultiplePlasticDeliveriesExist,
    findDeliveryByBoxUuid,
    findItemsInABox,
} from '@/src/common/contexts/utilities'

// this context handles visa gift box flow
type TGiftBoxState = {
    defaultImage: string
    currentStep: number
    setCurrentStep: Function
    denomination: string
    setDenomination: Function
    cardQuantity: number
    setCardQuantity: Function
    predesignUuid: string
    setPredesignUuid: Function
    customDesignObject: object
    setCustomDesignObject: Function
    designImage: string
    setDesignImage: Function
    designName: string
    setDesignName: Function
    messageOnCard: string
    setMessageOnCard: Function
    nameOnCard: string
    setNameOnCard: Function
    predesigns: IBACPredesign[]
    isLoading: boolean
    setIsLoading: Function
    continueIsLoading: boolean
    setContinueIsLoading: Function
    setDisplayLiveErrors: Function
    KYCMinMaxLimits: TKYCMinMaxValidations
    formErrors: ValidationError[]
    setFormErrors: Function
    validateAndAddCart: Function
    hideStep1: boolean
    isEditing: boolean
    selectedImageType: string
    setSelectedImageType: Function
}

export const PerfectGiftBoxContext = createContext<TGiftBoxState | null>(null)

export const usePerfectGiftBoxContext = (): TGiftBoxState => {
    const context = useContext(PerfectGiftBoxContext)

    if (!context) {
        throw new Error('Please use PerfectGiftBoxContext in parent component')
    }

    return context
}

export const PerfectGiftBoxProvider = ({
    predesigns,
    defaultImage,
    children,
    product,
}: {
    predesigns: IBACPredesign[]
    defaultImage: string
    children: ReactNode
    product: IBACProduct
}) => {
    const router = useRouter()
    const { refetch } = useGetCartManually()
    const { mutate: addCartMutate } = usePostAddCart()
    const { cartTotalLoad, cartTotalCards } = useCartContext()

    const [currentStep, setCurrentStep] = useState<number>(1)
    const [cardQuantity, setCardQuantity] = useState<number>(0)
    const [denomination, setDenomination] = useState<string>('')
    const [predesignUuid, setPredesignUuid] = useState<string>('')
    const [customDesignObject, setCustomDesignObject] = useState<{
        url: string
        asset_id: string
        public_id: string
        secure_url: string
    }>({
        url: '',
        asset_id: '',
        public_id: '',
        secure_url: '',
    })
    const [designImage, setDesignImage] = useState<string>('')
    const [designName, setDesignName] = useState<string>('')
    const [messageOnCard, setMessageOnCard] = useState<string>('')
    const [nameOnCard, setNameOnCard] = useState<string>('')

    const [displayLiveErrors, setDisplayLiveErrors] = useState<boolean>(false)
    const [formErrors, setFormErrors] = useState<ValidationError[]>([])

    // handles editing via cart
    const [boxItemUuidToEdit, setBoxItemUuidToEdit] = useState<string>()
    const [quantityFromCart, setQuantityFromCart] = useState<number>(0)
    const [editingDeliveryUuid, setEditingDeliveryUuid] = useState<
        string | null
    >(null)
    // this is used for KYC limit tracking
    const [itemDenomFromCart, setItemDenomFromCart] = useState<number>(0)

    const KYCMinMaxLimits = checkKYCMinMaxLimits({
        isEditing: !!boxItemUuidToEdit,
        totalValueLoadInCart: cartTotalLoad,
        totalNumberOfCardsInCart: cartTotalCards,
        itemDenomFromCart: itemDenomFromCart,
        newGiftTotalDenom: round(parseFloat(denomination) * cardQuantity, 2),
        originalCardQuantity: quantityFromCart,
        newCardQuantity: cardQuantity,
    })
    const [hideStep1, setHideStep1] = useState<boolean>(false)

    // used for the add to cart buttons
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [continueIsLoading, setContinueIsLoading] = useState<boolean>(false)

    //used for handling the active state of selected image button either predesign or custom
    const [selectedImageType, setSelectedImageType] = useState<string>('')

    // set states based on url params
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search)
        const step = urlParams.get('step')
        const boxUuid = urlParams.get('box_edit_uuid')
        const quantity = urlParams.get('quantity')

        if (step === '2' || boxUuid) {
            setHideStep1(true)
            setCurrentStep(2)
        }

        if (router.asPath === '/visa-gift-card-box') {
            setCurrentStep(1)
        }

        setDenomination(getDefaultDenom(50))
        quantity && setCardQuantity(Number(quantity))
        boxUuid && setBoxItemUuidToEdit(boxUuid)
    }, [router])

    useEffect(() => {
        getDefaultPredesign({
            predesigns: predesigns,
            setPredesignUuid: setPredesignUuid,
            setDesignImage: setDesignImage,
            setDesignName: setDesignName,
            defaultToPredesign: true,
        })
    }, [predesigns, setDesignImage, setDesignName, setPredesignUuid])

    useEffect(() => {
        if (!displayLiveErrors) {
            return
        }
        BoxStep1Schema.validate(
            {
                denomination,
                cardQuantity,
                designImage,
                nameOnCard,
                messageOnCard,
            },
            { abortEarly: false }
        )
            .then(() => {
                setFormErrors([])
            })
            .catch((err) => {
                console.error(err)
                setFormErrors(err.inner)
            })
    }, [
        cardQuantity,
        denomination,
        designImage,
        displayLiveErrors,
        messageOnCard,
        nameOnCard,
    ])

    // This effect loads a box for editing, if applicable.
    useEffect(() => {
        // If there is no query param with the box uuid, return
        if (!boxItemUuidToEdit) return

        // Do a fresh fetch of the cart
        refetch().then(async (response) => {
            const cartData = { ...(response.data as ICart) }

            // find the delivery that contains the box
            const deliveryWithBoxItems = findDeliveryByBoxUuid(
                cartData,
                boxItemUuidToEdit
            )

            // find only the items in this box
            const itemsForBox = findItemsInABox(cartData, boxItemUuidToEdit)

            setQuantityFromCart(itemsForBox.length)
            let singleLineItem
            if (deliveryWithBoxItems) {
                const { delivery_uuid } = deliveryWithBoxItems
                setEditingDeliveryUuid(delivery_uuid)
                setCardQuantity(itemsForBox.length)
                singleLineItem = itemsForBox[0]
            }

            if (singleLineItem) {
                setDenomination(singleLineItem.denomination)
                setItemDenomFromCart(
                    round(
                        parseFloat(singleLineItem.denomination) *
                            itemsForBox.length,
                        2
                    )
                )
                if (singleLineItem.design?.design_uuid) {
                    setPredesignUuid(singleLineItem.design.design_uuid)
                    setDesignImage(
                        singleLineItem.design.resolved_data?.design_preview_url
                    )
                    setDesignName(
                        singleLineItem.design.resolved_data?.design_name
                    )
                    setCustomDesignObject({
                        url: '',
                        asset_id: '',
                        public_id: '',
                        secure_url: '',
                    })
                }
                if (singleLineItem.custom_design?.url) {
                    setCustomDesignObject(singleLineItem.custom_design)
                    setDesignImage(singleLineItem.custom_design.url)
                    setDesignName(CUSTOM_CARD_IMAGE)
                }
                if (singleLineItem?.customization_data) {
                    setNameOnCard(
                        singleLineItem.customization_data?.name_on_card
                    )
                    setMessageOnCard(
                        singleLineItem.customization_data?.message_on_card
                    )
                }
            }
        })
    }, [boxItemUuidToEdit, refetch])

    const createCartObjectFromApiResponse = (
        response: QueryObserverResult
    ): ICart | IEmptyCart => {
        if (!response.data) {
            return {
                cart_contents: { deliveries: [] },
            } as IEmptyCart
        }
        return { ...(response.data as ICart) }
    }

    const assembleLineItems: Function = useCallback(async () => {
        let lineItemArray: ILineItemWithoutResolvedData[] = []

        let lineItem: ILineItemWithoutResolvedData = {
            line_item_uuid: '',
            line_item_type: LINE_ITEM_TYPE_CARD,
            denomination: denomination,
            item_price: denomination,
            product: {
                product_uuid: product.uuid,
            },
            design: {
                design_uuid: predesignUuid,
            },
            custom_design: {
                url: customDesignObject?.url ?? '',
                asset_id: customDesignObject?.asset_id ?? '',
                public_id: customDesignObject?.public_id ?? '',
                secure_url: customDesignObject?.secure_url ?? '',
            },
            customization_data: {
                name_on_card: nameOnCard,
                message_on_card: messageOnCard,
                carrier_from: '',
                carrier_to: '',
                carrier_message: '',
                carrier_type: 'no_carrier',
                box_uuid: boxItemUuidToEdit ?? uuid(),
            },
        }

        // this adds the line items to the delivery
        for (let i = 0; i < cardQuantity; i++) {
            lineItemArray.push({
                ...lineItem,
                line_item_uuid: uuid(),
            })
        }

        return lineItemArray
    }, [
        boxItemUuidToEdit,
        cardQuantity,
        customDesignObject,
        denomination,
        messageOnCard,
        nameOnCard,
        predesignUuid,
        product,
    ])

    const assembleDelivery = (lineItemData: ILineItem) => {
        return {
            delivery_uuid: editingDeliveryUuid ?? uuid(),
            delivery_option: {
                delivery_option_uuid: '',
            },
            delivery_details: {
                sender_name: '',
                recipient_name: '',
                recipient_message: '',
            },
            line_items: lineItemData,
        }
    }

    const addCart = useCallback(
        async (keepShopping: boolean) => {
            // Fetch the latest cart data from the RQ hook... then post a new cart with this new line_item.
            await refetch().then(async (response: QueryObserverResult) => {
                const cartData = createCartObjectFromApiResponse(response)
                const lineItems = await assembleLineItems()
                const deliveries = cartData.cart_contents.deliveries ?? []

                // check if there's an existing delivery for plastics
                const existingPlasticDeliveryIndex: number =
                    checkIfExistingDeliveryForPlastic(deliveries)

                // check if there are already multiple deliveries with only plastics
                const isMultiDelivery: boolean =
                    checkIfMultiplePlasticDeliveriesExist(deliveries)

                // if they're editing, update the existing box
                if (boxItemUuidToEdit) {
                    updateExistingBoxData({
                        cartData: cartData,
                        lineItems: lineItems,
                        boxUuid: boxItemUuidToEdit,
                        existingPlasticDeliveryIndex:
                            existingPlasticDeliveryIndex,
                    })
                } else if (isMultiDelivery) {
                    // add new delivery
                    cartData.cart_contents.deliveries.push(
                        // @ts-ignore
                        assembleDelivery(lineItems)
                    )
                } else if (existingPlasticDeliveryIndex >= 0) {
                    // if there is an existing delivery for plastics, add the box into it
                    cartData.cart_contents.deliveries[
                        existingPlasticDeliveryIndex
                    ].line_items.push(...lineItems)
                } else {
                    // if there isn't, add a new delivery for plastics
                    const newDelivery = assembleDelivery(lineItems)
                    // @ts-ignore
                    cartData.cart_contents.deliveries.push(newDelivery)
                }

                addCartMutate(cartData, {
                    onSuccess: async () => {
                        dataLayerAddVisaToCart(
                            ['eec.add', 'add_to_cart'],
                            MEDIUM_TYPE_PHYSICAL,
                            denomination,
                            cardQuantity,
                            designName,
                            true
                        )

                        if (keepShopping) {
                            await router.push(`${router.pathname}`)
                            router.reload()
                        } else {
                            await router.push(`/checkout`)
                        }
                    },
                    onError: () => {
                        alert('Unable to add item to cart. Please try again.')
                        setIsLoading(false)
                        setContinueIsLoading(false)
                    },
                })
            })
        },
        [
            addCartMutate,
            assembleDelivery,
            assembleLineItems,
            cardQuantity,
            denomination,
            designName,
            boxItemUuidToEdit,
            refetch,
            router,
        ]
    )

    const validateAndAddCart = useCallback(
        (keepShopping: boolean = false) => {
            BoxStep1Schema.validate(
                {
                    denomination,
                    cardQuantity,
                    designImage,
                    nameOnCard,
                    messageOnCard,
                },
                { abortEarly: false }
            )
                .then(async () => {
                    setFormErrors([])
                    await addCart(keepShopping)
                })
                .catch((err) => {
                    console.error(err)
                    setDisplayLiveErrors(true)
                    setFormErrors(err.inner)
                    setIsLoading(false)
                    setContinueIsLoading(false)
                })
        },
        [
            addCart,
            cardQuantity,
            denomination,
            designImage,
            messageOnCard,
            nameOnCard,
        ]
    )

    const values = useMemo(
        () => ({
            currentStep,
            denomination,
            cardQuantity,
            predesignUuid,
            customDesignObject,
            designImage,
            designName,
            defaultImage,
            messageOnCard,
            nameOnCard,
            predesigns,
            isLoading,
            continueIsLoading,
            displayLiveErrors,
            KYCMinMaxLimits,
            formErrors,
            hideStep1,
            isEditing: !!boxItemUuidToEdit,
            selectedImageType,
            setCurrentStep,
            setDenomination,
            setCardQuantity,
            setPredesignUuid,
            setCustomDesignObject,
            setDesignImage,
            setDesignName,
            setMessageOnCard,
            setNameOnCard,
            setIsLoading,
            setContinueIsLoading,
            setDisplayLiveErrors,
            setFormErrors,
            validateAndAddCart,
            setSelectedImageType,
        }),
        [
            currentStep,
            denomination,
            cardQuantity,
            predesignUuid,
            customDesignObject,
            designImage,
            designName,
            defaultImage,
            messageOnCard,
            nameOnCard,
            predesigns,
            isLoading,
            continueIsLoading,
            displayLiveErrors,
            formErrors,
            validateAndAddCart,
            hideStep1,
            boxItemUuidToEdit,
            KYCMinMaxLimits,
        ]
    )

    return (
        <PerfectGiftBoxContext.Provider value={values}>
            {children}
        </PerfectGiftBoxContext.Provider>
    )
}
